import { useState } from 'react'
import { Check, Plus, Sun, Moon, ChevronRight, Store, ExternalLink } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { ASSETS } from '@swim/config'
import { getWorkspaceColor } from '@swim/ui'
import { useTheme } from '@/providers/ThemeProvider'
import { useMyStreams } from '@/hooks/use-my-streams'
import { useLocaleNavigate } from '@/hooks/use-locale-navigate'
import {
  Drawer,
  DrawerContent,
  DrawerTitle,
  DrawerDescription,
} from '@/components/ui/drawer'

interface MobileLogoDrawerProps {
  open: boolean
  onClose: () => void
}

function WorkspaceAvatar({ initials, colorClass, avatarUrl }: { initials: string; colorClass: string; avatarUrl?: string | null }) {
  const [imgError, setImgError] = useState(false)
  return (
    <div className={`w-8 h-8 rounded-full flex-shrink-0 flex items-center justify-center overflow-hidden ${colorClass}`}>
      {avatarUrl && !imgError ? (
        <img src={avatarUrl} alt="" className="w-full h-full object-cover" onError={() => setImgError(true)} />
      ) : (
        <span className="text-[10px] font-bold text-white leading-none select-none">{initials}</span>
      )}
    </div>
  )
}

export function MobileLogoDrawer({ open, onClose }: MobileLogoDrawerProps) {
  const [activeId, setActiveId] = useState<string | null>(null)
  const [expandedId, setExpandedId] = useState<string | null>(null)
  const { theme, toggleTheme } = useTheme()
  const { t: tNav } = useTranslation('navigation')
  const { t } = useTranslation('common')
  const { data: myStreamsData } = useMyStreams()
  const { navigate } = useLocaleNavigate()

  const merchantPortalUrl = (import.meta as ImportMeta & { env: Record<string, string> }).env.VITE_MERCHANT_PORTAL_URL
    ?? 'https://merchant-staging.swimflows.com'

  const workspaces = (myStreamsData?.workspaces ?? []).map((ws) => ({
    id: ws.id,
    name: ws.businessName,
    avatarUrl: ws.avatarUrl,
    streams: ws.streams,
  }))

  const effectiveActiveId = activeId ?? workspaces[0]?.id ?? null

  function handleStreamClick(slug: string) {
    onClose()
    navigate(`/streams/${slug}`)
  }

  return (
    <Drawer open={open} onOpenChange={(o) => { if (!o) onClose() }}>
      <DrawerContent className="
        max-h-[85vh] flex flex-col
        bg-white dark:bg-slate-900
        border-sand-200 dark:border-slate-700
        rounded-t-2xl
      ">
        <DrawerTitle className="sr-only">{tNav('chrome.workspace.switcherAriaLabel')}</DrawerTitle>
        <DrawerDescription className="sr-only">{tNav('chrome.workspace.switcherAriaLabel')}</DrawerDescription>

        {/* Logo header */}
        <div className="flex items-center justify-center px-4 pt-3 pb-3 flex-shrink-0">
          <img
            src={ASSETS.logos.horizontal.black.md}
            alt={t('nav.logoAlt')}
            className="h-4 dark:hidden"
          />
          <img
            src={ASSETS.logos.horizontal.white.md}
            alt={t('nav.logoAlt')}
            className="h-4 hidden dark:block"
          />
        </div>

        <div className="flex-1 overflow-y-auto">
          {/* Workspaces — each row min 44px tall, expands to show its Streams (also min 44px each) */}
          <nav aria-label={tNav('chrome.workspace.label')} className="px-3 pb-2">
            <p className="text-[9px] font-mono tracking-widest uppercase text-slate-400 dark:text-slate-500 px-2 mb-2">
              {tNav('chrome.workspace.label')}
            </p>

            {workspaces.length > 0 ? workspaces.map((ws) => {
              const initials = ws.name.split(' ').map((w) => w[0] ?? '').join('').toUpperCase().slice(0, 2)
              const colorClass = getWorkspaceColor(ws.id)
              const isExpanded = expandedId === ws.id
              const isActive = effectiveActiveId === ws.id

              return (
                <div key={ws.id}>
                  <button
                    onClick={() => {
                      setActiveId(ws.id)
                      if (ws.streams.length > 0) setExpandedId(isExpanded ? null : ws.id)
                    }}
                    aria-expanded={isExpanded}
                    className={`
                      w-full min-h-[44px] flex items-center gap-3 px-3 py-3 rounded-xl transition-colors
                      ${isActive
                        ? 'bg-sand-100 dark:bg-slate-800'
                        : 'hover:bg-sand-50 dark:hover:bg-slate-800/60'
                      }
                    `}
                  >
                    <WorkspaceAvatar initials={initials} colorClass={colorClass} avatarUrl={ws.avatarUrl} />
                    <span className="flex-1 text-left text-sm font-medium font-sans text-slate-700 dark:text-slate-200 truncate">
                      {ws.name}
                    </span>
                    {isActive && !ws.streams.length && (
                      <Check className="w-4 h-4 text-ocean-500 dark:text-ocean-400 flex-shrink-0" aria-hidden />
                    )}
                    {ws.streams.length > 0 && (
                      <ChevronRight
                        aria-hidden
                        className={`w-4 h-4 flex-shrink-0 text-slate-400 dark:text-slate-500 transition-transform duration-200 ${isExpanded ? 'rotate-90' : ''}`}
                      />
                    )}
                  </button>

                  {/* Streams — CSS grid-rows trick animates height 0 -> auto, no JS measuring */}
                  <div
                    style={{
                      display: 'grid',
                      gridTemplateRows: isExpanded && ws.streams.length > 0 ? '1fr' : '0fr',
                      transition: 'grid-template-rows 0.2s ease',
                    }}
                  >
                    <div className="overflow-hidden">
                      <div className="pb-1">
                        {ws.streams.map((stream) => (
                          <button
                            key={stream.id}
                            onClick={() => handleStreamClick(stream.slug)}
                            className={`
                              w-full min-h-[44px] flex items-center gap-2.5 pl-11 pr-3 py-3
                              text-sm font-sans text-left
                              ${stream.status === 'INACTIVE' ? 'opacity-50' : ''}
                              text-slate-500 dark:text-slate-400
                              hover:bg-sand-50 dark:hover:bg-slate-800/60
                              transition-colors
                            `}
                          >
                            <img src={ASSETS.nav.streams} alt="" aria-hidden className="w-3.5 h-3.5 flex-shrink-0 opacity-60 dark:invert" />
                            <span className="truncate">{stream.name}</span>
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              )
            }) : (
              <p className="px-3 py-3 text-xs text-slate-400 dark:text-slate-500 font-sans">
                {t('chrome.workspace.noWorkspaces')}
              </p>
            )}

            {/* Add Workspace — opens merchant portal in a new tab, same as desktop */}
            <a
              href={`${merchantPortalUrl}/workspaces/new`}
              target="_blank"
              rel="noopener noreferrer"
              onClick={onClose}
              className="
                w-full min-h-[44px] flex items-center gap-3 px-3 py-3 rounded-xl mt-1
                text-slate-500 dark:text-slate-400
                hover:bg-sand-50 dark:hover:bg-slate-800/60
                transition-colors
              "
            >
              <div className="
                w-8 h-8 rounded-full flex-shrink-0
                border border-dashed border-slate-300 dark:border-slate-600
                flex items-center justify-center
              ">
                <Plus className="w-4 h-4 text-slate-400" aria-hidden />
              </div>
              <span className="text-sm font-sans">{t('chrome.workspace.addWorkspace')}</span>
            </a>

            {/* Merchant Portal — only shown once the user has at least one workspace, matches desktop */}
            {workspaces.length > 0 && (
              <a
                href={merchantPortalUrl}
                target="_blank"
                rel="noopener noreferrer"
                onClick={onClose}
                className="
                  w-full min-h-[44px] flex items-center gap-3 px-3 py-3 rounded-xl
                  text-sm font-sans font-medium
                  text-ocean-500 dark:text-ocean-400
                  hover:bg-sand-50 dark:hover:bg-slate-800/60
                  transition-colors
                "
              >
                <div className="w-8 h-8 flex-shrink-0 flex items-center justify-center">
                  <Store className="w-4 h-4" aria-hidden />
                </div>
                <span className="flex-1">{t('chrome.workspace.merchantPortal')}</span>
                <ExternalLink className="w-3.5 h-3.5 flex-shrink-0 opacity-50" aria-hidden />
              </a>
            )}
          </nav>
        </div>

        {/* Sticky footer — Light / Dark icon toggles */}
        <div className="
          flex-shrink-0
          border-t border-sand-200 dark:border-slate-700
          px-5 py-3
          flex items-center justify-between
        ">
          <span className="text-xs font-sans text-slate-400 dark:text-slate-500">
            {t('chrome.mobile.appearance')}
          </span>

          <div className="flex items-center gap-1 p-1 bg-sand-100 dark:bg-slate-800 rounded-full">
            <button
              onClick={() => theme === 'dark' && toggleTheme()}
              aria-label={t('chrome.switchToLight')}
              className={`
                w-8 h-8 rounded-full flex items-center justify-center transition-colors
                ${theme === 'light'
                  ? 'bg-white shadow-sm text-amber-500'
                  : 'text-slate-400 hover:text-slate-500 dark:hover:text-slate-300'
                }
              `}
            >
              <Sun className="w-4 h-4" aria-hidden />
            </button>
            <button
              onClick={() => theme === 'light' && toggleTheme()}
              aria-label={t('chrome.switchToDark')}
              className={`
                w-8 h-8 rounded-full flex items-center justify-center transition-colors
                ${theme === 'dark'
                  ? 'bg-slate-700 shadow-sm text-ocean-300'
                  : 'text-slate-400 hover:text-slate-500'
                }
              `}
            >
              <Moon className="w-4 h-4" aria-hidden />
            </button>
          </div>
        </div>

        {/* Safe area for home indicator on iPhone */}
        <div className="flex-shrink-0 h-safe-bottom pb-2" />
      </DrawerContent>
    </Drawer>
  )
}
